import { Component, OnInit, OnDestroy, EventEmitter, Output, forwardRef, AfterViewInit } from '@angular/core';
import {
  FormControl,
  Validators,
  FormGroup,
  FormBuilder,
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  Validator,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Logger } from '@app/logging.service';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { UsAddressLookupService } from '../../services/us-address-lookup.service';
import { UtilityDetailsService } from '../../services/utility-details.service';
import { Store } from '@ngrx/store';
import { Address } from '@app/shared/models/address';
import { AppState } from '@app/state/app.state';
import { SetAddress, ClearUtilityInformation, ClearUtility } from '@utility-information/state/actions/utility-information.actions';
import { QuoteRequestType } from '@app/shared/models/QuoteRequestType';
import { quoteRequestTypeSelector } from '@utility-information/state/selectors/utility-information.selectors';
import { isReadOnlySelector } from '@app/state/app.selectors';

@Component({
  selector: 'app-zip-lookup',
  templateUrl: './zip-lookup.component.html',
  styleUrls: ['./zip-lookup.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ZipLookupComponent),
      multi: true,

    },   
  ],
})
export class ZipLookupComponent implements OnInit, ControlValueAccessor, Validator, OnDestroy {
  @Output() formInitialised = new EventEmitter<FormGroup>();
  @Output() addressFound = new EventEmitter<Address>();
  
  routeSubscription$: any;
  quoteRequestId: any;

  public frmZipLookup: FormGroup;
  public working = false;
  private logger: Logger;
  register$: Subscription;
  cityValueChanges$: Subscription;

  public onTouched = () => {};
  public onChange = (value: any) => {};

  zipCodeValueChanges$: Subscription;
  lookup$: Subscription;
  zipLookupValueChanges$: Subscription;
  selectQuoteRequestType$: Subscription;
  quoteRequestType: QuoteRequestType;
  private isReadOnly$: Subscription;
  isReadOnly: boolean;

  constructor(
    private usLookup: UsAddressLookupService,
    private utilityDetailsService: UtilityDetailsService,
    private fb: FormBuilder,
    private store: Store<AppState>,
  ) {
    this.logger = Logger.for(this);
  }

  ngOnDestroy(): void {
    this.zipCodeValueChanges$?.unsubscribe();
    this.lookup$?.unsubscribe();
    this.zipLookupValueChanges$?.unsubscribe();
    this.register$?.unsubscribe();
    this.cityValueChanges$?.unsubscribe();
    this.selectQuoteRequestType$?.unsubscribe();
    this.isReadOnly$?.unsubscribe();
  }

  public searchForState() {
    this.working = true;
    const ctrlZip = this.frmZipLookup.get('zipCode');
    const ctrlState = this.frmZipLookup.get('state');
    const ctrlCity = this.frmZipLookup.get('city');
    const ctrlmunicipalityCode = this.frmZipLookup.get('municipalityCode');

    if (ctrlZip.value != null && ctrlZip.value != "")
    {
      this.lookup$ = this.usLookup.lookupStateFromZipCode(ctrlZip.value).subscribe(
        (results) => {
          
          this.working = false;
          if (!results || !results.success || !results.stateCode) {
            ctrlZip.setErrors({ invalidZip: true });
            ctrlState.setValue('');
            this.onChange(this.frmZipLookup.value);
            this.store.dispatch(new SetAddress(this.quoteRequestId, undefined, undefined));
            this.store.dispatch(new ClearUtilityInformation(this.quoteRequestId));
            this.store.dispatch(new ClearUtility(this.quoteRequestId));
            return;
          }
  
          this.utilityDetailsService.stateFoundFromZipCode.emit(results.stateCode);
  
          ctrlState.setValue(results.stateCode);
          ctrlCity.setValue(results.city);
          ctrlmunicipalityCode.setValue(results.municipalityCode);
          
          this.addressFound.emit({
            zipCode: ctrlZip.value,
            city: results.city,
            state: results.stateCode,
            municipalityCode: results.municipalityCode,
          });
  
          this.onChange(this.frmZipLookup.value);
          this.store.dispatch(new SetAddress(this.quoteRequestId, ctrlZip.value, results.stateCode));
        },
        (err) => {
          
          ctrlZip.setErrors({ serviceErr: true });
          ctrlState.setValue('');
          this.working = false;
          this.logger.error(err);
          this.onChange(this.frmZipLookup.value);
          this.store.dispatch(new SetAddress(this.quoteRequestId, undefined, undefined));
          this.store.dispatch(new ClearUtilityInformation(this.quoteRequestId));
          this.store.dispatch(new ClearUtility(this.quoteRequestId));
        }

      );

    }
    else 
    {
      ctrlState.setValue('');
      ctrlZip.setValue('');
      ctrlCity.setValue('');
      this.working = false;
      this.onChange(this.frmZipLookup.value);
      this.store.dispatch(new SetAddress(this.quoteRequestId, undefined, undefined));
      this.store.dispatch(new ClearUtilityInformation(this.quoteRequestId));
      this.store.dispatch(new ClearUtility(this.quoteRequestId));
    }    
  }

  ngOnInit(): void {
    this.selectQuoteRequestType$ = this.store.select(quoteRequestTypeSelector).subscribe((x) => {
      this.quoteRequestType = x as QuoteRequestType;
    });

    const zipCode = (this.quoteRequestType === QuoteRequestType.QuickQuote) 
                    ? new FormControl('', Validators.compose([Validators.minLength(5), Validators.maxLength(7)]))
                    : new FormControl('', Validators.compose([Validators.required, Validators.minLength(5), Validators.maxLength(7)]));
                    
    const city = new FormControl('', Validators.required);
    const state = new FormControl('', Validators.required);

    this.frmZipLookup = this.fb.group({
      addressLine1: [''],
      addressLine2: [''],
      zipCode,
      city,
      state,
      country: [''],
      municipalityCode: [''],
    });

    this.zipCodeValueChanges$ = zipCode.valueChanges.pipe(debounceTime(250), distinctUntilChanged()).subscribe((x) => {
      
      if (!zipCode.disabled) {
        if (zipCode.valid) {
          this.searchForState();
        }
        if (!zipCode.valid) {
          this.frmZipLookup.get('state').setValue('');
          this.frmZipLookup.get('city').setValue('');
          this.store.dispatch(new SetAddress(this.quoteRequestId, undefined, undefined));
          this.store.dispatch(new ClearUtilityInformation(this.quoteRequestId));
          this.store.dispatch(new ClearUtility(this.quoteRequestId));
          this.onChange(this.frmZipLookup.value);
        }
      }
    });

    this.cityValueChanges$ = city.valueChanges.pipe(debounceTime(250), distinctUntilChanged()).subscribe((x) => {
      if (!city.disabled) {
        this.onChange(this.frmZipLookup.value);
      }
    });

    this.isReadOnly$ = this.store.select(isReadOnlySelector).subscribe((isReadOnly) => {
      this.isReadOnly = isReadOnly;
      if (this.isReadOnly) {
        this.frmZipLookup.disable({ emitEvent: false });
      } 
    });

    this.formInitialised.emit(this.frmZipLookup);
  }

  writeValue(val: any): void {
    if (val) this.frmZipLookup.patchValue(val, { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(control: AbstractControl): ValidationErrors {
    if (control.errors) {
      this.frmZipLookup.get('zipCode').markAsTouched();
      this.frmZipLookup.get('zipCode').markAsDirty();
    }
    return this.frmZipLookup.valid ? null : { valid: false, message: 'Zip Lookup Details Invalid' };
  }

  registerOnValidatorChange?(fn: () => void): void {}

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.frmZipLookup.get('zipCode').disable({ emitEvent: false });
      this.frmZipLookup.get('city').disable({ emitEvent: false });
      this.frmZipLookup.get('state').disable({ emitEvent: false });
    } else {
      this.frmZipLookup.get('zipCode').enable({ emitEvent: false });
      this.frmZipLookup.get('city').enable({ emitEvent: false });
      this.frmZipLookup.get('state').enable({ emitEvent: false });
    }
  }
}
